import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import $api from "@/zapi-frontend/shared/api/axios";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import { getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";
import {
  CreateProjectChat,
  normalizeCaseBriefLanguageUserMessages,
  type ChatStep,
  type ChatUploadedFile,
  type CaseBriefCompleteness,
  type CreateProjectChatRef,
  type TFrameProgress
} from "@/zapi-frontend/widgets/CreateProjectChat";
import { useCaseBriefChatStore } from "@/zapi-frontend/stores/caseBriefChatStore";
import type { TPreUploadedDocument } from "@/zapi-frontend/pages/CreateProjectPage/model/usePreUploadDocuments";
import type { TDocumentStatus } from "@/zapi-frontend/shared/types/shared";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasAdminPrivileges, hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments";
import { useProjectDimensions } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { useLanguages, LANGUAGE_CODE_TO_VALUE } from "@/zapi-frontend/shared/services/useLanguages";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { AnalysisSettingsModal } from "@/zapi-frontend/widgets/CreateProjectChat/ui/AnalysisSettingsModal";
import type { ProjectErrors, TProjectDataWithoutRepository } from "@/zapi-frontend/widgets/CreateProjectForm/model/types";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { NewProjectView } from "@/zapi-frontend/pages/CreateProjectPage/ui/NewProject/NewProjectView";
import { useOrganizationTags } from "@/zapi-frontend/pages/CreateProjectPage/ui/NewProject/useOrganizationTags";
import { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { useProjectLanguageSync } from "@/zapi-frontend/shared/i18n/useProjectLanguageSync";
import {
  caseBriefDocumentPath,
  openVaultDocument,
  projectDocumentPath
} from "@/zapi-frontend/shared/lib/openVaultDocument";
import { removeTimestampPrefix } from "@/zapi-frontend/shared/lib/splitFileName";

type TProjectResponse = {
  id: number;
  name: string;
  organizationId: number;
  versionDisplayName: string;
  versionGroupId: string | null;
  versions: { id: string; name: string; date: string; isDraft?: boolean }[];
  caseBriefMdContent: string | null;
  caseBriefId: number | null;
  projectInstructions: string | null;
  presentationPrompt: string | null;
  shortDescription: string | null;
  language: string;
  projectTemplateSlug: string;
  iconShape: string | null;
  iconColor: string | null;
  logoUrl: string | null;
  visibility: "organization" | "private";
  dimensionValues: Record<string, string>;
  projectChapters: Array<{
    id: number;
    chapterInstructions: string | null;
    projectTemplateChapter: {
      slug: string;
      name: string;
      icon?: string | null;
    } | null;
  }>;
  documents: Array<{
    id: number;
    name: string;
    status: TDocumentStatus;
    fileSize?: number;
    createdAt?: string;
    webUrl?: string | null;
  }>;
};

type TCaseBriefResponse = {
  mdContent: string | null;
  language: string;
  chatStep: string | null;
  pendingDimensionIndex: number | null;
  chatMessages: { id: string; role: "user" | "assistant"; content: string }[] | null;
  vectorStoreId: string | null;
  uploadedFiles: { fileId?: string; fileName: string; documentId: number; fileSize: number; webUrl?: string }[] | null;
  generatingStatus?: string | null;
  completeness?: CaseBriefCompleteness | null;
};

type TMode = "newVersion" | "relaunch";

type TProps = {
  mode?: TMode;
};

const AUTOSAVE_DEBOUNCE_MS = 1000;

const ALL_LANGUAGE_OPTIONS = [
  { label: "English", value: "english" },
  { label: "Japanese", value: "japanese" }
];
// The project card names each language in its own script, as on the create page.
const NATIVE_LANGUAGE_LABELS: Record<string, string> = { english: "English", japanese: "日本語" };

const LEGACY_VERSION_WELCOME = "Your draft is ready. Make your changes and launch the analysis when you're ready.";

// Carries the templateKey so the chat re-resolves it against the project language on every render.
const VERSION_WELCOME_CONTENT = JSON.stringify({
  type: "answer",
  templateKey: "versionDraftReady",
  content: LEGACY_VERSION_WELCOME
});

function isVersionWelcomeMessage(message: { role: string; content: string }): boolean {
  if (message.role !== "assistant") return false;
  try {
    const parsed = JSON.parse(message.content);
    return (
      parsed?.templateKey === "versionDraftReady" ||
      parsed?.type === "version_name_prompt" ||
      parsed?.content === LEGACY_VERSION_WELCOME
    );
  } catch {
    return false;
  }
}

export function NewVersionPage({ mode = "newVersion" }: TProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("createProject");
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = Number(projectId);
  const { role, accessMode, isLabs } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const currentOrganizationId = useOrganizationStore((s) => s.currentOrganizationId);
  const { modelName: selectedModel, setModelName: setSelectedModel } = useChatStore();
  const setSetupTask = useProjectSetupStore((s) => s.setSetupTask);
  const setPolling = useProjectSetupStore((s) => s.setPolling);

  const queryClient = useQueryClient();
  const versionsQueryKey = useMemo(() => ["project-new-version", projectIdNum] as const, [projectIdNum]);

  const { data, isLoading, error } = useQuery<TProjectResponse>({
    queryKey: versionsQueryKey,
    queryFn: async () => {
      const res = await $api.get<{ data: TProjectResponse }>(`/projects/${projectIdNum}`);
      return res.data.data;
    },
    enabled: Number.isFinite(projectIdNum) && projectIdNum > 0
  });

  useProjectLanguageSync(data?.language);

  const handleOptimisticRename = useCallback(
    (versionId: string, newName: string) => {
      queryClient.setQueryData<TProjectResponse>(versionsQueryKey, (old) =>
        old
          ? {
              ...old,
              versionDisplayName: versionId === String(projectIdNum) ? newName : old.versionDisplayName,
              versions: old.versions?.map((v) => (v.id === versionId ? { ...v, name: newName } : v))
            }
          : old
      );
    },
    [queryClient, versionsQueryKey, projectIdNum]
  );

  const { data: orgDimensions = [] } = useProjectDimensions();
  const sortedDimensions = useMemo(
    () => [...orgDimensions].sort((a, b) => a.sortOrder - b.sortOrder),
    [orgDimensions]
  );

  const { languages } = useLanguages();
  const availableLanguages = useMemo(() => {
    const configuredValues = new Set(
      languages.map((l) => LANGUAGE_CODE_TO_VALUE[l.code] ?? l.code.toLowerCase()).filter(Boolean)
    );
    if (configuredValues.size === 0) return ALL_LANGUAGE_OPTIONS;
    return ALL_LANGUAGE_OPTIONS.filter((lang) => configuredValues.has(lang.value));
  }, [languages]);
  const { documents, createDocument, createWebLink, deleteDocument, refetchDocuments } = useDocuments(
    projectId || "",
    undefined,
    3000
  );

  const [content, setContent] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [isLaunching, setIsLaunching] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [caseBriefId, setCaseBriefId] = useState<number | null>(null);
  const [isRestoringBrief, setIsRestoringBrief] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<TPreUploadedDocument[]>([]);
  const [versionDisplayName, setVersionDisplayName] = useState<string>("");
  const [completeness, setCompleteness] = useState<CaseBriefCompleteness | null>(null);
  // Set once Leo calls the frame ready, so the launch dialog never says otherwise.
  const [essentialsCovered, setEssentialsCovered] = useState(false);
  const handleEssentialsCovered = useCallback(() => setEssentialsCovered(true), []);
  const [initialGeneratingStatus, setInitialGeneratingStatus] = useState<string | null>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [skipPrefill, setSkipPrefill] = useState(false);
  const [settingsErrors, setSettingsErrors] = useState<ProjectErrors>({});
  const [projectInstructions, setProjectInstructions] = useState<string>("");
  const [chaptersInstructions, setChaptersInstructions] = useState<Record<string, string>>({});
  const [presentationPrompt, setPresentationPrompt] = useState<string>("");
  const [dimensionValues, setDimensionValues] = useState<Record<string, string>>({});
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<"organization" | "private">("organization");
  const [frameProgress, setFrameProgress] = useState<TFrameProgress>({
    activeSection: null,
    writingSection: null,
    coveredUpTo: null
  });
  const [launchConfirmOpen, setLaunchConfirmOpen] = useState(false);
  const caseBriefRestored = useRef(false);
  const chatRef = useRef<CreateProjectChatRef | null>(null);

  useEffect(() => {
    if (!data || hydrated) return;
    setContent(data.caseBriefMdContent ?? "");
    setProjectName(data.name);
    setProjectDescription(data.shortDescription ?? "");
    setVersionDisplayName(data.versionDisplayName ?? "");
    setProjectInstructions(data.projectInstructions ?? "");
    setPresentationPrompt(data.presentationPrompt ?? "");
    setDimensionValues(data.dimensionValues ?? {});
    setLogoUrl(data.logoUrl ?? null);
    setVisibility(data.visibility ?? "organization");
    setChaptersInstructions(
      (data.projectChapters ?? []).reduce<Record<string, string>>((acc, ch) => {
        const slug = ch.projectTemplateChapter?.slug;
        if (slug) acc[slug] = ch.chapterInstructions ?? "";
        return acc;
      }, {})
    );
    setUploadedDocuments(
      (data.documents ?? []).map((doc) => ({
        documentId: doc.id,
        fileName: doc.name,
        status: doc.status,
        fileSize: doc.fileSize,
        createdAt: doc.createdAt ? new Date(doc.createdAt) : undefined,
        webUrl: doc.webUrl ?? undefined
      }))
    );
    setHydrated(true);
  }, [data, hydrated]);

  useEffect(() => {
    if (data?.versionDisplayName != null) setVersionDisplayName(data.versionDisplayName);
  }, [data?.versionDisplayName]);

  const isTemplateLess = data?.projectTemplateSlug === "";

  const vaultDocuments: TPreUploadedDocument[] = useMemo(
    () =>
      documents.map((d) => ({
        documentId: d.id,
        fileName: d.fileName,
        fileSize: d.fileSize,
        webUrl: d.webUrl ?? undefined,
        status: d.status as TDocumentStatus
      })),
    [documents]
  );

  const handleDimensionChange = useCallback(
    (dimensionId: number, value: string) => {
      setDimensionValues((prev) => {
        const next = { ...prev };
        if (value.trim() === "") delete next[String(dimensionId)];
        else next[String(dimensionId)] = value;
        return next;
      });
      if (!Number.isFinite(projectIdNum)) return;
      const updated = { ...dimensionValues };
      if (value.trim() === "") delete updated[String(dimensionId)];
      else updated[String(dimensionId)] = value;
      $api
        .patch(`/projects/${projectIdNum}`, { dimensionValues: updated })
        .catch((err) => console.error("Failed to save dimension value:", err));
    },
    [projectIdNum, dimensionValues]
  );

  const handlePhotoChange = useCallback(
    async (logoBase64: string) => {
      if (!Number.isFinite(projectIdNum)) return;
      await $api.patch(`/projects/${projectIdNum}`, { logoBase64 });
      setLogoUrl(logoBase64);

      const { selectedProject, setSelectedProject } = useProjectStore.getState();
      if (selectedProject && selectedProject.id === String(projectIdNum)) {
        setSelectedProject({
          ...selectedProject,
          logoUrl: logoBase64,
          photoAuthor: null,
          photoLicense: null,
          photoSourceUrl: null
        });
      }
    },
    [projectIdNum]
  );

  const handleVisibilityChange = useCallback(
    (next: "organization" | "private") => {
      setVisibility(next);
      if (!Number.isFinite(projectIdNum)) return;
      $api
        .patch(`/projects/${projectIdNum}`, { visibility: next })
        .catch((err) => console.error("Failed to save project visibility:", err));
    },
    [projectIdNum]
  );

  const handleLanguageChange = useCallback(
    async (lang: string) => {
      queryClient.setQueryData<TProjectResponse>(versionsQueryKey, (old) =>
        old ? { ...old, language: lang } : old
      );
      useCaseBriefChatStore.getState().setLanguage(lang);
      if (!Number.isFinite(projectIdNum)) return;

      // The brief's headings and field labels are translated server-side, so the editor is
      // refreshed from the response rather than keeping the old language's structure. The
      // debounced autosave then carries the translated text onto the project row.
      if (caseBriefId && content) {
        try {
          const res = await $api.patch<{ mdContent?: string }>(
            "/chat/create-project-chat/update-case-brief-language",
            { caseBriefId, language: lang, mdContent: content }
          );
          if (res.data.mdContent) setContent(res.data.mdContent);
        } catch (err) {
          console.error("Failed to update case brief language:", err);
        }
      }

      $api
        .patch(`/projects/${projectIdNum}`, { language: lang })
        .catch((err) => console.error("Failed to save project language:", err));
    },
    [queryClient, versionsQueryKey, projectIdNum, caseBriefId, content]
  );

  useEffect(() => {
    if (!data?.caseBriefId || caseBriefRestored.current) return;
    caseBriefRestored.current = true;
    const cbId = data.caseBriefId;
    setIsRestoringBrief(true);
    useCaseBriefChatStore.getState().reset();
    useCaseBriefChatStore.getState().setCaseBriefId(cbId);
    setCaseBriefId(cbId);

    if (mode === "newVersion") {
      const welcomeMsg = {
        id: crypto.randomUUID(),
        role: "assistant" as const,
        content: VERSION_WELCOME_CONTENT
      };
      useCaseBriefChatStore.getState().setMessages([welcomeMsg]);
      useCaseBriefChatStore.getState().setStep("chat" as ChatStep);
    }

    $api
      .get<TCaseBriefResponse>(`/chat/create-project-chat/case-brief/${cbId}`)
      .then((res) => {
        if (res.data.language) useCaseBriefChatStore.getState().setLanguage(res.data.language);
        if (res.data.chatStep && res.data.chatStep !== "language") {
          useCaseBriefChatStore.getState().setStep(res.data.chatStep as ChatStep);
        }
        if (typeof res.data.pendingDimensionIndex === "number") {
          useCaseBriefChatStore.getState().setPendingDimensionIndex(res.data.pendingDimensionIndex);
        }
        if (res.data.vectorStoreId) {
          useCaseBriefChatStore.getState().setVectorStoreId(res.data.vectorStoreId);
        }
        setInitialGeneratingStatus(res.data.generatingStatus ?? null);
        if (res.data.completeness) setCompleteness(res.data.completeness);
        if (res.data.uploadedFiles && Array.isArray(res.data.uploadedFiles) && res.data.uploadedFiles.length > 0) {
          useCaseBriefChatStore.getState().setUploadedFiles(res.data.uploadedFiles);
          setUploadedDocuments((prev) => {
            const existingIds = new Set(prev.map((d) => d.documentId));
            const extra = res.data.uploadedFiles!.filter((f) => !existingIds.has(f.documentId)).map((f) => ({
              documentId: f.documentId,
              fileName: f.fileName,
              status: "done" as TDocumentStatus,
              fileSize: f.fileSize,
              webUrl: f.webUrl
            }));
            return extra.length > 0 ? [...prev, ...extra] : prev;
          });
        }
        const restoredMsgs = res.data.chatMessages ?? [];
        const normalizedRestored = restoredMsgs.map((m) =>
          isVersionWelcomeMessage(m) ? { ...m, content: VERSION_WELCOME_CONTENT } : m
        );
        const hasWelcome = normalizedRestored.some(isVersionWelcomeMessage);
        const hasUserMessage = normalizedRestored.some((m) => m.role === "user");
        const shouldInjectWelcome = mode === "newVersion" && !hasWelcome && !hasUserMessage;
        if (shouldInjectWelcome) {
          const welcomeMsg = {
            id: crypto.randomUUID(),
            role: "assistant" as const,
            content: VERSION_WELCOME_CONTENT
          };
          const merged = [welcomeMsg, ...normalizedRestored];
          useCaseBriefChatStore.getState().setMessages(normalizeCaseBriefLanguageUserMessages(merged));
        } else if (normalizedRestored.length > 0) {
          useCaseBriefChatStore.getState().setMessages(normalizeCaseBriefLanguageUserMessages(normalizedRestored));
        }
      })
      .catch((err) => console.error("[NewVersionPage] case brief restore failed:", err))
      .finally(() => setIsRestoringBrief(false));
  }, [data?.caseBriefId]);

  useEffect(() => {
    if (!hydrated || !data) return;
    const briefUnchanged = content === (data.caseBriefMdContent ?? "");
    const nameUnchanged = projectName === data.name;
    const descUnchanged = projectDescription === (data.shortDescription ?? "");
    if (briefUnchanged && nameUnchanged && descUnchanged) return;
    const timer = setTimeout(() => {
      $api
        .patch(`/projects/${projectIdNum}`, {
          caseBriefMdContent: content,
          ...(projectName.trim() && projectName !== data.name ? { name: projectName } : {}),
          ...(projectDescription !== (data.shortDescription ?? "") ? { shortDescription: projectDescription } : {})
        })
        .then(() => {
          // The header's breadcrumb reads the project from the store.
          const { selectedProject, setSelectedProject } = useProjectStore.getState();
          if (
            projectName.trim() &&
            selectedProject?.id === String(projectIdNum) &&
            selectedProject.name !== projectName
          )
            setSelectedProject({ ...selectedProject, name: projectName });
        })
        .catch((err) => console.error("New Version autosave failed:", err));
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [content, projectName, projectDescription, data, hydrated, projectIdNum]);

  useEffect(() => {
    if (!hydrated || caseBriefId === null) return;
    if (content === (data?.caseBriefMdContent ?? "")) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      $api
        .post<CaseBriefCompleteness>("/chat/create-project-chat/completeness", {
          caseBriefContent: content,
          caseBriefId
        })
        .then((res) => {
          if (!cancelled) setCompleteness(res.data);
        })
        .catch((err) => console.error("Failed to fetch completeness:", err));
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [content, hydrated, caseBriefId, data?.caseBriefMdContent]);

  useEffect(() => {
    if (caseBriefId === null) return;
    const lastSavedSnapshotRef = { current: "" };
    const getSnapshot = (state: { messages: { id: string; content: string }[] }) => {
      const last = state.messages[state.messages.length - 1];
      return `${state.messages.length}:${last?.id ?? ""}:${last?.content?.length ?? 0}`;
    };
    const unsub = useCaseBriefChatStore.subscribe((state) => {
      if (isRestoringBrief) return;
      if (state.caseBriefId !== caseBriefId) return;
      if (!state.messages.length) return;
      if (!state.messages.some((m) => m.role === "user")) return;
      const snap = getSnapshot(state);
      if (snap === lastSavedSnapshotRef.current) return;
      lastSavedSnapshotRef.current = snap;
      $api
        .patch("/chat/create-project-chat/update-case-brief-content", {
          caseBriefId,
          mdContent: content,
          chatMessages: state.messages
        })
        .catch((err) => console.error("Failed to autosave chat messages:", err));
    });
    return unsub;
  }, [caseBriefId, content, isRestoringBrief]);

  const handleLaunch = async () => {
    if (!data) return;
    setIsLaunching(true);
    try {
      await $api.patch(`/projects/${projectIdNum}`, {
        projectInstructions,
        ...(isTemplateLess ? {} : { chaptersInstructions }),
        presentationPrompt
      });
      const setupRes = await $api.post<{ taskId: number }>(`/projects/${projectIdNum}/auto-setup`, {
        prefill: !skipPrefill,
        ...(selectedModel ? { model: selectedModel } : {})
      });
      if (setupRes.data?.taskId) {
        setSetupTask(String(projectIdNum), setupRes.data.taskId);
        setPolling(String(projectIdNum), true);
      }
      toast.success(t("launch.launched"));
      navigate(getRouteProjectDetails(projectIdNum));
    } catch (err) {
      console.error("Failed to launch analysis:", err);
      toast.error(t("launch.failed"));
    } finally {
      setIsLaunching(false);
    }
  };

  const handleVersionNameSave = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === versionDisplayName || !Number.isFinite(projectIdNum)) return;
    try {
      await $api.patch(`/projects/${projectIdNum}`, { versionDisplayName: trimmed });
      handleOptimisticRename(String(projectIdNum), trimmed);
      setVersionDisplayName(trimmed);
      void useProjectStore.getState().fetchSelectedProject(String(projectIdNum), true);
    } catch (err) {
      console.error("Failed to rename the version:", err);
      toast.error(t("versions.errors.renameFailed", { ns: "common" }));
    }
  };

  const handleFileUploaded = (file: ChatUploadedFile) => {
    setUploadedDocuments((prev) => [
      ...prev,
      { documentId: file.documentId, fileName: file.fileName, status: "done" as TDocumentStatus, fileSize: file.fileSize }
    ]);
  };

  const handleRemoveDocument = (documentId: number) => {
    setUploadedDocuments((prev) => prev.filter((d) => d.documentId !== documentId));
  };

  const handleAddWebLink = (url: string) => {
    setUploadedDocuments((prev) => [
      ...prev,
      { documentId: -Date.now(), fileName: url, status: "done" as TDocumentStatus, webUrl: url }
    ]);
  };

  const ensureCaseBrief = async () => caseBriefId ?? 0;

  const organizationTags = useOrganizationTags({
    organizationId: currentOrganizationId,
    dimensions: sortedDimensions,
    values: dimensionValues,
    onValueCleared: (dimensionId) => handleDimensionChange(dimensionId, "")
  });

  if (isLoading || !data) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="flex min-h-[calc(100svh-var(--app-header-height))] items-center justify-center text-muted-foreground">
        Failed to load project.
      </div>
    );
  }

  const title = mode === "relaunch" ? t("titles.reviewRelaunch") : t("titles.newVersion");
  const caseBriefProgress = completeness?.overallScore ?? 0;
  const hasMinimumBrief = projectName.trim().length > 0 && !!data.language;
  const computedBriefStatus: "empty" | "incomplete" | "complete" = !hasMinimumBrief
    ? "empty"
    : essentialsCovered
    ? "complete"
    : "incomplete";

  const setupDocuments = vaultDocuments.length > 0 ? vaultDocuments : uploadedDocuments;

  const settingsProjectData: TProjectDataWithoutRepository = {
    name: projectName,
    description: projectDescription,
    language: data.language,
    projectTemplateSlug: data.projectTemplateSlug,
    chaptersInstructions,
    projectInstructions,
    presentationPrompt,
    dimensionValues
  };

  const chat = caseBriefId !== null && (
    <CreateProjectChat
      chatRef={chatRef}
      caseBriefId={caseBriefId}
      isRestoring={isRestoringBrief}
      readOnly={!hasEdit}
      onDocumentUpdate={setContent}
      onFileUploaded={handleFileUploaded}
      onRemoveDocument={handleRemoveDocument}
      onAddWebLink={handleAddWebLink}
      uploadedDocuments={uploadedDocuments}
      onEnsureCaseBrief={ensureCaseBrief}
      briefStatus={computedBriefStatus}
      onEssentialsCovered={handleEssentialsCovered}
      isLaunching={isLaunching}
      onLaunchAnalysis={handleLaunch}
      progress={caseBriefProgress}
      currentBriefContent={content}
      externalLanguage={data.language}
      initialGeneratingStatus={initialGeneratingStatus}
      leoPanel={{
        language: data.language,
        onRequestLaunch: () => setLaunchConfirmOpen(true),
        onFrameProgress: setFrameProgress,
        onDetailsReading: () => undefined
      }}
    />
  );

  return (
    <>
      <NewProjectView
        title={title}
        readOnly={!hasEdit}
        restoring={isRestoringBrief}
        chat={chat}
        project={{
          name: projectName,
          description: projectDescription,
          pending: false,
          onNameChange: setProjectName,
          onDescriptionChange: setProjectDescription,
          picture: logoUrl ? { url: logoUrl } : null,
          pictureRemovable: false,
          onPictureChange: (dataUrl) => {
            if (dataUrl) void handlePhotoChange(dataUrl);
          },
          language: data.language,
          languageOptions: availableLanguages.map((option) => ({
            value: option.value,
            label: NATIVE_LANGUAGE_LABELS[option.value] ?? option.label
          })),
          onLanguageChange: (lang) => void handleLanguageChange(lang),
          versionName: versionDisplayName,
          onVersionNameChange: (name) => void handleVersionNameSave(name),
          visibility,
          onVisibilityChange: handleVisibilityChange,
          dimensions: sortedDimensions,
          dimensionValues,
          onDimensionChange: handleDimensionChange,
          tagManagement: hasAdminPrivileges(role) ? organizationTags : undefined
        }}
        vault={{
          documents: setupDocuments,
          pendingNames: [],
          onAddFiles: async (files) => {
            for (const file of files) {
              await createDocument(file);
            }
            await refetchDocuments();
          },
          onAddLink: async (url) => {
            const trimmed = url.trim();
            const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
            await createWebLink(normalized);
            await refetchDocuments();
          },
          onOpen: (documentId) => {
            const document = setupDocuments.find((doc) => doc.documentId === documentId);
            if (!document) return;
            // The version's own files belong to the project; uploads made before it had any belong to the case brief.
            const filePath = documents.some((doc) => doc.id === documentId)
              ? projectDocumentPath(projectIdNum, documentId)
              : caseBriefId === null
              ? null
              : caseBriefDocumentPath(caseBriefId, documentId);
            openVaultDocument(document, filePath).catch(() =>
              toast.error(t("failedOpen", { name: document.webUrl ?? removeTimestampPrefix(document.fileName) }))
            );
          },
          onRemove: async (documentId) => {
            await deleteDocument(documentId);
            await refetchDocuments();
          },
          readingIds: []
        }}
        frame={{
          brief: content,
          onChange: setContent,
          completeness,
          progress: caseBriefProgress,
          activeSection: frameProgress.activeSection,
          writingSection: frameProgress.writingSection,
          coveredUpTo: frameProgress.coveredUpTo,
          showTemplatePrompts: false,
          caseBriefId,
          lang: projectLanguageToLng(data.language)
        }}
        launch={{
          status: computedBriefStatus,
          decisionPending: false,
          isLaunching,
          confirmOpen: launchConfirmOpen,
          onConfirmOpenChange: setLaunchConfirmOpen,
          onLaunch: () => {
            setLaunchConfirmOpen(false);
            void handleLaunch();
          }
        }}
        onOpenSettings={isLabs ? () => setIsSettingsModalOpen(true) : undefined}
      />

      {isLabs && (
        <AnalysisSettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
          onSave={({
            projectData: updated,
            selectedModel: updatedModel,
            skipPrefill: updatedSkipPrefill
          }) => {
            setProjectName(updated.name);
            setProjectDescription(updated.description ?? "");
            setProjectInstructions(updated.projectInstructions ?? "");
            setPresentationPrompt(updated.presentationPrompt ?? "");
            setChaptersInstructions(updated.chaptersInstructions ?? {});
            setDimensionValues(updated.dimensionValues ?? {});
            setSelectedModel(updatedModel);
            setSkipPrefill(updatedSkipPrefill);
          }}
          projectData={settingsProjectData}
          errors={settingsErrors}
          setErrors={setSettingsErrors}
          selectedModel={selectedModel}
          skipPrefill={skipPrefill}
        />
      )}
    </>
  );
}
